import { DeedNonprofit } from 'generated/graphql';

type NonprofitWithIndex = {
  nonprofit: DeedNonprofit;
  originalIndex: number;
};

/**
 * Filters nonprofits by EIN groups to remove duplicates, favoring active PPGF nonprofits, and returns a list of nonprofits sorted by original index
 *
 * @param nonprofits - The list of nonprofits to filter
 * @returns A list of filtered nonprofits sorted by original index
 */
export const filterNonprofits = (nonprofits: Array<DeedNonprofit | null>): DeedNonprofit[] => {
  if (!nonprofits?.length) {
    return [];
  }

  const nonprofitsWithIndices = nonprofits.filter(Boolean).map((nonprofit, index) => ({
    nonprofit,
    originalIndex: index,
  }));
  const chapters = nonprofitsWithIndices.filter(({ nonprofit }) => nonprofit.isChapter);
  const nonChapters = nonprofitsWithIndices.filter(({ nonprofit }) => !nonprofit.isChapter);

  const withEin = nonChapters.filter(({ nonprofit }) => nonprofit.ein != null);
  const withoutEin = nonChapters.filter(({ nonprofit }) => nonprofit.ein == null);

  const einGroups = withEin.reduce((acc, { nonprofit, originalIndex }) => {
    const { ein } = nonprofit;

    // NOTE-EV: This is safe because we already filtered out nonprofits without EINs
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (!acc[ein!]) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acc[ein!] = [];
    }

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    acc[ein!].push({ nonprofit, originalIndex });

    return acc;
  }, {} as Record<string, NonprofitWithIndex[]>);

  const filteredWithEin = Object.values(einGroups).flatMap((group) => {
    const ppgfNonprofit = group.find(
      ({ nonprofit }) =>
        nonprofit.payPalModel === 'PPGF' && nonprofit.paypalCharityType === 'PPGF_ENROLLED'
    );

    return ppgfNonprofit ? [ppgfNonprofit] : group;
  });

  const allNonprofits = [...chapters, ...withoutEin, ...filteredWithEin]
    .sort((nonprofitA, nonprofitB) => nonprofitA.originalIndex - nonprofitB.originalIndex)
    .map(({ nonprofit }) => nonprofit);

  return allNonprofits;
};
